import { afterDataThrough } from "@nexus/widget-kit";
import type { NetworkIdGroupCount, NetworkIdWeeklyData } from "./types";

export const DEFAULT_LOW_SAMPLE_THRESHOLD = 30;
const DAY_MS = 86_400_000;

export interface WeekModel {
  weekStart: string;
  /** ISO week label, "W42". */
  label: string;
  total: number;
  partial: boolean;
  lowSample: boolean;
  /** Not measured yet: the week starts at or after the data's end. */
  missing: boolean;
}

export interface GroupShare {
  group: string;
  share: number;
}

export interface WeeklyMax {
  group: string;
  /** Highest weekly share among full, well-sampled weeks; null when none qualifies. */
  share: number | null;
  week: string | null;
}

export interface WeeklyModel {
  weeks: WeekModel[];
  /** Ranked by period total (ties by name); a group is "network_group network_sub_level". */
  groups: string[];
  /** Network group and sub-level behind each displayed group. */
  families: Map<string, { group: string; subLevel: string | null }>;
  /** `counts[groupIndex][weekIndex]`. */
  counts: number[][];
  /** `shares[groupIndex][weekIndex]`, percent of the week's detections. */
  shares: number[][];
  total: number;
  /** At most two groups with the most detections over the period. */
  top: GroupShare[];
  /** Remainder after the top two; null when there is no other group. */
  otherShare: number | null;
  weeklyMax: WeeklyMax[];
}

/** ISO week number of the week starting on `weekStart` (a Monday, `YYYY-MM-DD`). */
export function isoWeekLabel(weekStart: string): string {
  const thursday = new Date(Date.parse(`${weekStart}T00:00:00Z`) + 3 * DAY_MS);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return `W${Math.floor((thursday.getTime() - yearStart) / DAY_MS / 7) + 1}`;
}

/** Displayed name: the network group followed by its sub-level, when it has one. */
export function groupLabel({ networkGroup, networkSubLevel }: NetworkIdGroupCount): string {
  return networkSubLevel ? `${networkGroup} ${networkSubLevel}` : networkGroup;
}

/** A week the source zero-filled although it has no data for it yet. */
export function isMissingWeek(
  week: NetworkIdWeeklyData["weeks"][number],
  data: NetworkIdWeeklyData,
): boolean {
  return afterDataThrough(Date.parse(`${week.weekStart}T00:00:00Z`), data.freshness?.dataThrough);
}

/** Derives every displayed figure from the raw weekly counts. */
export function buildWeeklyModel(
  data: NetworkIdWeeklyData,
  lowSampleThreshold: number = DEFAULT_LOW_SAMPLE_THRESHOLD,
): WeeklyModel {
  const threshold = Math.max(1, lowSampleThreshold);
  const totals = new Map<string, number>();
  const families: WeeklyModel["families"] = new Map();
  for (const week of data.weeks)
    for (const entry of week.groups) {
      const label = groupLabel(entry);
      totals.set(label, (totals.get(label) ?? 0) + entry.count);
      families.set(label, { group: entry.networkGroup, subLevel: entry.networkSubLevel });
    }
  const groups = [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([group]) => group);
  const position = new Map(groups.map((group, index) => [group, index]));

  const weeks = data.weeks.map((week) => ({
    weekStart: week.weekStart,
    label: isoWeekLabel(week.weekStart),
    total: week.total,
    partial: week.partial,
    lowSample: week.total < threshold,
    missing: isMissingWeek(week, data),
  }));
  const counts = groups.map(() => weeks.map(() => 0));
  data.weeks.forEach((week, weekIndex) => {
    for (const entry of week.groups) {
      const groupIndex = position.get(groupLabel(entry));
      if (groupIndex !== undefined) counts[groupIndex][weekIndex] = entry.count;
    }
  });
  const shares = counts.map((row) =>
    row.map((count, weekIndex) =>
      weeks[weekIndex].total > 0 ? (count / weeks[weekIndex].total) * 100 : 0,
    ),
  );

  const total = groups.reduce((sum, group) => sum + (totals.get(group) ?? 0), 0);
  const top = groups.slice(0, 2).map((group) => ({
    group,
    share: total > 0 ? ((totals.get(group) ?? 0) / total) * 100 : 0,
  }));
  const otherShare =
    groups.length > 2 ? Math.max(0, 100 - top.reduce((sum, item) => sum + item.share, 0)) : null;

  const weeklyMax = top.map(({ group }) => {
    const row = shares[position.get(group) ?? 0];
    let best: WeeklyMax = { group, share: null, week: null };
    weeks.forEach((week, weekIndex) => {
      if (week.partial || week.lowSample) return;
      if (best.share === null || row[weekIndex] > best.share)
        best = { group, share: row[weekIndex], week: week.label };
    });
    return best;
  });

  return { weeks, groups, families, counts, shares, total, top, otherShare, weeklyMax };
}
